import { apiGet, apiPost, apiPut } from '../../lib/api'
import { bookQuery, type BookAsk } from '../../lib/bookAsk'
import { withAuth } from '../auth/token'
import type { FmcsaVerification } from '../fmcsa/api'

/**
 * Mirrors the broker slices on the API. The template forbids enums, so the API's
 * string states arrive as unions — which is what they are on the wire anyway.
 */
export type ComplianceState = 'Pending' | 'Active' | 'Blocked'

export type BrokerSource = 'InPlatform' | 'Dat'

export interface Address {
  line1: string
  line2: string | null
  city: string
  state: string
  postalCode: string
}

export interface BrokerSummary {
  id: string
  name: string
  mcNumber: string | null
  city: string
  state: string
  complianceState: ComplianceState
  active: boolean
}

export interface BrokerDetail {
  id: string
  name: string
  mcNumber: string | null
  usdotNumber: string | null
  taxId: string | null
  bondAmount: number
  creditRating: string | null
  source: BrokerSource
  address: Address
  phone: string | null
  email: string | null
  complianceState: ComplianceState
  active: boolean
  deactivatedAt: string | null
  /** What the federal register said last time, or null if nobody has asked. */
  fmcsaVerification: FmcsaVerification | null
  createdAt: string
  updatedAt: string
}

/** What the registration form posts — every optional field may travel as null. */
export interface BrokerPayload {
  name: string
  mcNumber: string | null
  usdotNumber: string | null
  taxId: string | null
  bondAmount: number
  creditRating: string | null
  address: {
    line1: string
    line2: string | null
    city: string
    state: string
    postalCode: string
  }
  phone: string | null
  email: string | null
  /**
   * The `updatedAt` the form was opened on. Required on an edit: a save made on an
   * older reading is refused, so a form left open cannot put back what somebody else
   * saved since. Only a save of this form moves it — not the compliance evaluation,
   * not a paper landing, not an activation.
   */
  version?: string
}

export function listBrokers(ask: BookAsk = {}): Promise<BrokerSummary[]> {
  return apiGet<BrokerSummary[]>(`/api/brokers${bookQuery(ask)}`, { headers: withAuth() })
}

/** The book searched for a picker that may name any broker — a filter's. Module-level, so its identity holds. */
export const searchBrokers = (term: string, limit: number) => listBrokers({ search: term, limit })

/** The book searched for a picker that files something under a broker: only the ones we work with. */
export const searchActiveBrokers = (term: string, limit: number) => listBrokers({ search: term, limit, active: true })

export function getBroker(id: string): Promise<BrokerDetail> {
  return apiGet<BrokerDetail>(`/api/brokers/${id}`, { headers: withAuth() })
}

export function registerBroker(payload: BrokerPayload): Promise<BrokerDetail> {
  return apiPost<BrokerDetail>('/api/brokers', payload, { headers: withAuth() })
}

export function updateBroker(id: string, payload: BrokerPayload): Promise<BrokerDetail> {
  return apiPut<BrokerDetail>(`/api/brokers/${id}`, payload, { headers: withAuth() })
}
